import FroalaEditor from 'froala-editor';
import { Observable } from 'rxjs';
import { FroalaEvent } from '@r1-compass-library/models/froala/froala-event';

export type ImageAssetType = 'play' | 'content';
export interface ImageAssetRef {
    assetId: string;
    assetType: ImageAssetType;
}
export interface ImageMeta extends Partial<ImageAssetRef> {
    fileName: string;
}

export type FroalaEventHandler = (this: FroalaEditor, ...args: never[]) => unknown;
export type FroalaEvents = Partial<Record<FroalaEvent, FroalaEventHandler>>;

export type FroalaFieldMergeDroppedEventData = {
    event: DragEvent;
    editor: FroalaEditor;
    fieldMergeElement: HTMLElement;
};

export type FroalaFieldMergeDeletedEventData = {
    event: MouseEvent;
    editor: FroalaEditor;
    fieldMergeElement: HTMLElement;
};

export type FroalaFieldMergeDroppedCallback = (data: FroalaFieldMergeDroppedEventData) => void;
export type FroalaFieldMergeDeletedCallback = (data: FroalaFieldMergeDeletedEventData) => void;

export class FroalaEditorOptions {
    attribution?: boolean;
    charCounterCount?: boolean;
    charCounterMax?: number;
    codeBeautifierOptions?: CodeBeautifierOptions;
    codeMirror?: boolean;
    colorsBackground?: string[];
    colorsButtons?: string[];
    colorsText?: string[];
    dragInline?: boolean;
    enter?: any;
    events?: any;
    fieldMergeDeleted?: FroalaFieldMergeDeletedCallback;
    fieldMergeDropped?: FroalaFieldMergeDroppedCallback;
    htmlAllowedAttrs?: string[];
    height?: number;
    heightMax?: number;
    heightMin?: number;
    iconsTemplate?: string;
    imageDefaultWidth?: number;
    imageEditButtons?: string[];
    imageInsertButtons?: string[];
    imageOutputSize?: boolean;
    imageUpload?: boolean;
    imageUploadRemoteUrls?: boolean;
    imageUploadURL?: string;
    key?: string;
    lineBreakerOffset?: number;
    lineBreakerTags?: string[];
    linkEditButtons?: string[];
    linkInsertButtons?: string[];
    linkList?: any;
    paragraphFormat?: ParagraphFormat;
    placeholderText?: string;
    pluginsEnabled?: string[];
    tableEditButtons?: string[];
    toolbarButtons?: string[];
    toolbarSticky?: boolean;
    useClasses?: boolean;
    wordAllowedStyleProps?: string[];
    wordDeniedAttrs?: string[];
    wordDeniedTags?: string[];
    wordPasteModal?: boolean;
    wordPasteKeepFormatting?: boolean;
    zIndex?: number;
}

export class CodeBeautifierOptions {
    brace_style?: string;
    end_with_newline?: boolean;
    extra_liners?: string;
    indent_char?: string;
    indent_inner_html?: boolean;
    indent_size?: number;
    wrap_line_length?: number;
}

export class ParagraphFormat {
    N?: string;
    H1?: string;
    H2?: string;
    H3?: string;
}

export type FroalaElementWrapper = {
    length?: number;
    attr?: (name: string, val?: string) => unknown;
    addClass?: (className: string) => unknown;
    removeAttr?: (name: string) => unknown;
    remove?: () => void;
    [index: number]: Element;
};

export type FroalaImageUploadHandlers = {
    /** Perform the actual upload (e.g. S3 presigned URL transfer) and return the image URL/metadata */
    uploadImage$(file: File, fileName: string): Observable<{ url: string }>;
    /** Optional side-effect when an image is successfully uploaded/pasted */
    onUploadSuccess?: (fileName: string) => void;
    /** Optional side-effect when an upload fails */
    onUploadError?: (fileName: string, error: unknown) => void;
    /** Optional side-effect when an image is removed/deleted from the editor */
    onImageRemoved?: () => void;
    /** Optional custom HTML builder if a component needs custom <img> formatting */
    buildImageHtml?: (url: string, fileName: string) => string;
    /** Returns current asset ID (e.g. play ID or content ID) */
    getAssetId?: () => string;
    /** Returns current asset type ('play' | 'content') */
    getAssetType?: () => 'play' | 'content';
    /** Copies an existing S3 object into the current asset. Returns a presigned GET URL for the new object. */
    copyImageToCurrentAsset$(
        source: ImageAssetRef & { fileName: string },
        targetFileName: string,
        pastedSrc: string
    ): Observable<File>;
    getAssetRef(): ImageAssetRef;
};
